/** @jsx jsx */
import {
  BaseButton,
  Button,
  Grid,
  IconButton,
  Modal,
  Spacer,
  Table,
  TextGroupMedium,
  useAsyncEffect,
  useMediaQuery,
} from "@cd2/components/build/es6"
import { FileUploader, useFileboy } from "@cd2/fileboy-react"
import { Fields, Form2 } from "@cd2/forms/build/es6"
import { H4, P, TextBlock } from "@cd2/text/build/es6"
import { assert } from "@cd2/tools/build/es6"
import { css, jsx } from "@emotion/core"
import { add, format } from "date-fns"
import React, { useEffect, useState } from "react"
import { ConfirmCancelButtons } from "../../../Components/Buttons/ConfirmCancelButtons"
import { CreateButton } from "../../../Components/Buttons/CreateButton"
import { DownloadLinkButton } from "../../../Components/Buttons/DownloadLink"
import { ButtonsWrapper } from "../../../Components/ButtonsWrapper"
import { themeColors } from "../../../Components/EOSThemeProvider"
import { FloatingBox } from "../../../Components/Misc/FloatingBox"
import { ContactSelectList } from "../../../Components/SelectLists/ContactSelectList"
import { Form2ModelSelect } from "../../../Components/SupportTicketForm"
import { LoadingTable } from "../../../Components/Tables/utils"
import { buttonConfigs } from "../../../constants/buttonConfigs"
import { icons } from "../../../constants/icons"
import { useConfirmation } from "../../../hooks/useConfirmation"
import { useConfirmDelete } from "../../../hooks/useConfirmCancelConfirmation"
import { useSiteAlerts } from "../../../hooks/useSiteAlerts"
import { HoverCustomerLoader } from "../../../hoverLoaders/HoverCustomerLoader"
import { HoverQuoteLoader } from "../../../hoverLoaders/HoverQuoteLoader"
import { HoverSalesRepLoader } from "../../../hoverLoaders/HoverSalesRepLoader"
import { FileboyFilePreviewColumn } from "../../../NewComponents/Columns"
import { fileTypes } from "../../../types/common"
import { IRecord } from "../../../types/schema"
import { sum } from "../../../utils/arrays"
import { getTotals } from "../../../utils/getQuoteOrderTotals"
import { currencyNameToSymbol, formatPrice } from "../../../utils/utils"
import { useContactCreateForCustomerConfirmation } from "../../Customers/CustomerShowContactsPage"
import { fieldEvents } from "@cd2/forms/build/es6/Form2"
import {
  CustomerSelectList2,
  ICustomerSelect2,
} from "../../../Components/SelectLists/CustomerSelectList2"
import { buildCustomerSearchFilter } from "../../../../shared/search"

export type ICustomerDropdownOption = {
  label: string
  value: string
  data: IRecord<"customers", "name"> & {
    branch: IRecord<"branches", "name">
    sales_rep: IRecord<"sales_reps", "name">
  }
}

export interface IProjectData {
  id: string
  name: string
  percentCertainty: number | null
  customer: string | null
  sales_rep: string | null
  dueDate: Date | null
  stage: string
  contacts: string[]
  files: string[]
  quotes: string[]
  notes: string
  followUpDate?: Date
  rolloutValue: Record<string, { date: number; value: number }>
  projectValue?: number | null
}

export const projectStages = ["Quoted", "Run Rate", "Ordered", "Discussion", "Won", "Lost"].map(
  v => ({
    label: v,
    value: v,
  }),
)

export const percentCertaintyOptions = [100, 75, 50, 25, 0].map(v => ({
  label: `${v}%`,
  value: v,
}))

export function ProjectsForm({
  data,
  isEditPage,
  onSubmit,
}: {
  data?: Partial<IProjectData>
  isEditPage?: boolean
  onSubmit: (data: IProjectData) => void
}) {
  const [small, smallDevice] = useMediaQuery([{ maxWidth: 1700 }, { maxWidth: 1400 }])
  const [manageFileNode, handleManageFile] = useProjectsFiles()
  const skeem = window.skeem
  const alerts = useSiteAlerts()

  const [contactFormNode, _getContactInformation, handleCreateContactFromNode] =
    useContactCreateForCustomerConfirmation()

  async function handleCreateContact(customer: string) {
    assert(customer)
    const customerRecord = await skeem.fetch2<
      IRecord<"customers", "name" | "isGenericCustomer"> & { branch: IRecord<"branches"> }
    >({
      first: {
        name: "customers",
        attributes: ["name", "branch", "isGenericCustomer"],
        filter: { matches: { id: customer } },
      },
    })
    try {
      const contact = await handleCreateContactFromNode(customerRecord)
      if (contact) {
        return contact.id
      }
    } catch (e) {
      alerts.genericError(JSON.stringify(e))
    }
  }

  function handleFormatSubmit(data: IProjectData) {
    const formatted = { ...data }
    formatted.rolloutValue = Object.entries(data.rolloutValue).reduce((acc, [key, value]) => {
      if (value.value !== null && value.date) {
        acc[key] = value
      }
      return acc
    }, {} as IProjectData["rolloutValue"])
    return onSubmit(formatted)
  }

  return (
    <div>
      {contactFormNode}
      {manageFileNode}
      <Form2.Form
        initialData={data ?? { files: [], quotes: [], contacts: [] }}
        onSubmit={handleFormatSubmit}
      >
        <Form2.WithForm
          subscribe={{ dataChange: true }}
          render={form => {
            const formData = form.getData() as IProjectData
            return (
              <div>
                <Grid
                  columns={{ repeat: { times: "auto-fit", size: 200 } }}
                  gap={8}
                  css={css`
                    background-color: #e5e5e5;
                    padding: 8px 16px;
                  `}
                >
                  <ValidateCustomerPresence />
                  <Form2.WithField<string>
                    field="customer"
                    render={field => {
                      const [customerOption, setCustomerOption] = useState<ICustomerSelect2 | null>(
                        null,
                      )

                      useAsyncEffect(async () => {
                        if (!field.getValue()) {
                          setCustomerOption(null)
                          return
                        }
                        const record = await skeem.fetch2<ICustomerSelect2>({
                          first: {
                            name: "customers",
                            attributes: CustomerSelectList2.attributes,
                            filter: { matches: { id: field.getValue() } },
                          },
                        })
                        setCustomerOption(record)
                        const salesRepField = form.getField("sales_rep")
                        if (record.sales_rep && !salesRepField.getValue()) {
                          salesRepField.setValue(record.sales_rep.id)
                        }
                      }, [field.getValue()])

                      return (
                        <CustomerSelectList2
                          key={"customer-select"}
                          label="Customer"
                          validations={{ presence: true }}
                          onChange={(_, opt) => {
                            setCustomerOption(opt ?? null)
                            field.setValue(opt?.id)
                          }}
                          value={customerOption}
                          disabled={!!formData.contacts?.join(".")}
                          searchFilter={(query: string) => ({
                            or: [
                              buildCustomerSearchFilter(query),
                              {
                                exists: {
                                  path: [
                                    {
                                      name: "addresses",
                                      filter: {
                                        and: [
                                          {
                                            matches: { showInCustomerAddressOptions: true },
                                          },
                                          {
                                            like: [
                                              {
                                                stringReplace: [{ attr: "postcode" }, " ", ""],
                                              },
                                              { value: query.replace(/\s/g, "") },
                                            ],
                                          },
                                        ],
                                      },
                                    },
                                  ],
                                },
                              },
                            ],
                          })}
                          isClearable={
                            !((!!data?.customer && isEditPage) || !!formData.contacts?.join("."))
                          }
                          includeBranchName
                          allBranches
                        />
                      )
                    }}
                  />

                  {formData.customer ? (
                    <Form2ModelSelect
                      key={formData?.customer ?? "sales-rep-select"}
                      field="sales_rep"
                      model="sales_reps"
                      labelAttribute="name"
                      label="Sales Rep"
                      validations={{ presence: true }}
                    />
                  ) : (
                    <div>
                      <Fields.Select options={[]} label="Sales Rep" disabled />
                    </div>
                  )}
                  <Form2.Date
                    field="dueDate"
                    placeholderText={format(add(new Date(), { months: 1 }), "dd/MM/yyyy")}
                    validations={{ presence: true }}
                  />
                  {!isEditPage && (
                    <Form2.Date
                      field="followUpDate"
                      placeholderText={format(add(new Date(), { months: 1 }), "dd/MM/yyyy")}
                      validations={{ presence: true }}
                    />
                  )}

                  <Form2.Select
                    field="stage"
                    label="Stage"
                    options={projectStages}
                    valueExtractor={(opt: { value: any }) => opt.value}
                    getOptionLabel={opt => opt.label}
                    getOptionValue={opt => opt.value}
                    validations={{ presence: true }}
                  />
                  <StageCertaintyWatcher />
                </Grid>
                <Grid
                  columns={smallDevice ? undefined : [{ fr: 2 }, { fr: 4 }, { fr: 2 }]}
                  css={css`
                    border-top: 2px solid #ddd;
                    border-bottom: 2px solid #ddd;
                  `}
                >
                  <div
                    css={css`
                      background-color: #f5f5f5;
                      padding: 12px;
                    `}
                  >
                    <FloatingBox header={<H4>Contacts</H4>}>
                      <Form2.WithField<string[]>
                        field="contacts"
                        subscribe={{ change: true }}
                        defaultValue={[]}
                        render={contactsField => (
                          <ContactsLoader contactIds={contactsField.getValue()} />
                        )}
                      />
                      <Spacer />

                      <React.Fragment>
                        {form.getData()?.customer ? (
                          <Grid columns={small ? undefined : ["auto", "max-content"]} gap={4}>
                            <ContactSelectList
                              key={form.getData().contacts?.join(".")}
                              label="Add existing contact"
                              onChange={contact => {
                                const contactField = form.getField("contacts")
                                contactField.setValue(
                                  (contactField.getValue() ?? []).concat(contact),
                                )
                              }}
                              customerId={form.getData().customer}
                              skeemFilter={{
                                not: { matches: { id: { or: form.getData().contacts } } },
                              }}
                              disabled={!form.getData()?.customer}
                            />
                            <div>
                              <CreateButton
                                onClick={() => {
                                  const formData = form.getData()
                                  handleCreateContact(formData.customer).then(result => {
                                    if (result) {
                                      form
                                        .getField("contacts")
                                        .setValue([...(formData.contacts ?? []), result])
                                    }
                                  })
                                }}
                                buttonStyle="text"
                                extendTheme={{ padding: 8, fontSize: 12 }}
                                color="#666"
                                disabled={!form.getData()?.customer}
                              >
                                Create new contact
                              </CreateButton>
                            </div>
                          </Grid>
                        ) : (
                          <P>Select a customer to add contacts</P>
                        )}
                      </React.Fragment>
                    </FloatingBox>
                  </div>

                  <div
                    css={css`
                      background-color: white;
                      border-left: 1px solid #ddd;
                      border-right: 1px solid #ddd;
                      padding: 16px;
                    `}
                  >
                    <Form2.Text field="name" validations={{ presence: true }} />
                    <Spacer />
                    <Form2.Select
                      field="percentCertainty"
                      prefix="%"
                      options={percentCertaintyOptions}
                      valueExtractor={(opt: { value: any }) => opt.value}
                      getOptionLabel={opt => opt.label}
                      getOptionValue={opt => opt.value as any}
                      validations={{ presence: true }}
                    />
                    <Spacer />
                    <div>
                      <Form2.Money field="projectValue" asPennies />
                      <P size="small">
                        Specify a value for this project. This value is used in estimates unless
                        quotes are added.
                      </P>
                    </div>
                    <Spacer />
                    {/* <Form2.TextArea field="notes" rows="auto" />
                    <Spacer /> */}
                    <FloatingBox
                      header={<H4>Quotes</H4>}
                      footer={
                        formData.customer ? (
                          <Form2.WithField<string[]>
                            subscribe={true}
                            field="quotes"
                            render={field => (
                              <QuoteSelect
                                customerId={formData.customer}
                                quoteIds={field.getValue() ?? []}
                                onChange={quote =>
                                  field.setValue([...(field.getValue() ?? []), quote])
                                }
                              />
                            )}
                          />
                        ) : (
                          <P>Select a customer to add quotes</P>
                        )
                      }
                    >
                      <QuotesLoader quoteIds={formData.quotes} />
                    </FloatingBox>
                    <Spacer />
                    <FilesLoader
                      fileIds={formData.files ?? []}
                      handleManageFile={handleManageFile}
                    />
                    <Spacer />
                    <ValueFields />
                  </div>

                  <div
                    css={css`
                      background-color: #f5f5f5;
                      padding: 12px;
                    `}
                  >
                    <Grid gap={16}>
                      {formData.customer && <HoverCustomerLoader id={formData.customer} inline />}
                      {formData.sales_rep && <HoverSalesRepLoader id={formData.sales_rep} inline />}
                    </Grid>
                  </div>
                </Grid>
              </div>
            )
          }}
        />
        <div
          css={css`
            padding-left: 12px;
          `}
        >
          <Form2.WithForm
            render={form => (
              <BaseButton {...buttonConfigs.formSaveButton} disabled={form.state.isSubmitting} />
            )}
            subscribe={{ submitting: true }}
          />
        </div>
      </Form2.Form>
    </div>
  )
}

function ValidateCustomerPresence() {
  Form2.useFormSubmitLock(state => {
    const customerField = state.getField("customer")
    const value = customerField.getValue()
    if (!value) {
      customerField.setError("must be present")
      return false
    }
  })
  return null
}

export function StageCertaintyWatcher() {
  const stageField = Form2.useField<string>("stage", { subscribe: true })
  const percentCertaintyField = Form2.useField<number | null>("percentCertainty", {
    subscribe: true,
  })
  useEffect(() => {
    stageField.events.on(fieldEvents.change, () => {
      console.log("stage changed", stageField.getValue())
      if (stageField.getValue() === "Won") {
        percentCertaintyField.setValue(100)
      } else if (stageField.getValue() === "Lost") {
        percentCertaintyField.setValue(0)
      }
    })
  }, [])

  return null
}

function QuoteSelect({
  customerId,
  quoteIds,
  onChange,
}: {
  customerId?: string | null
  quoteIds: string[]
  onChange(quote: string): void
}) {
  const [reloadKey, setReloadKey] = useState(0)
  const skeem = window.skeem

  function loadQuotes(query: string) {
    if (!customerId) {
      return Promise.resolve([])
    }
    // add customer ref, value, PO number
    return skeem.fetch2<IQuote[]>({
      model: {
        name: "quotes",
        attributes: iQuoteAttributes,
        paginate: { size: 10 },
        sort: { by: "created_at", direction: "desc" },
        filter: {
          and: [
            { matches: { customer: customerId } },
            { matches: { identifier: { like: query } } },
            { not: { matches: { id: { or: quoteIds } } } },
            { not: { exists: { attr: "projects" } } },
          ],
        },
      },
    })
  }

  return (
    <Fields.AsyncSelect
      key={[customerId, reloadKey].join("-")}
      label="Add a quote"
      onChange={value => {
        onChange(value.id)
        setReloadKey(k => k + 1)
      }}
      loadOptions={loadQuotes}
      formatOptionLabel={opt => (
        <div>
          <P>{opt.identifier}</P>
          <P size="small">
            Total Price:{" "}
            {formatPrice(
              getTotals(opt.items, opt.carriage_pennies, opt.customer?.tax_rate).totalPricePennies,
              { currency: currencyNameToSymbol(opt.customer.currency_name), isPennies: true },
            )}
          </P>
          {opt.customer_reference && <P size="small">Customer Ref: {opt.customer_reference}</P>}
          {opt.customer_purchase_order_reference && (
            <P size="small">PO Ref: {opt.customer_purchase_order_reference}</P>
          )}
        </div>
      )}
      getOptionLabel={opt => opt.identifier}
      getOptionValue={opt => opt.id}
      noOptionsMessage={({ inputValue }) =>
        inputValue.length === 0 ? "Type to search..." : "No results"
      }
      disabled={!customerId}
    />
  )
}

type IFile = IRecord<"files", "fileName" | "fileboyId">

function FilesLoader({
  fileIds,
  handleManageFile,
}: {
  fileIds: string[]
  handleManageFile: (data?: IFile) => Promise<IFile | null>
}) {
  const filesField = Form2.useField<string[]>("files", { subscribe: true })
  const alerts = useSiteAlerts()
  const filesFormField = Form2.useField<string[]>("files", { subscribe: true, defaultValue: [] })
  const skeem = window.skeem
  const fileboy = useFileboy()
  const [files, setFiles] = useState<IFile[]>([])

  async function loadFiles() {
    if (!fileIds.length) {
      setFiles([])
      return
    }
    const files = await skeem.fetch2<IFile[]>({
      model: {
        name: "files",
        attributes: ["fileName", "fileboyId"],
        filter: { matches: { id: { or: fileIds } } },
      },
    })
    setFiles(files)
  }

  useAsyncEffect(async () => {
    await loadFiles()
  }, [JSON.stringify(fileIds)])

  const [confirmDeleteNode, handleConfirmDelete] = useConfirmDelete({
    heading: "Delete file?",
    body: <P>This will permanently remove the file</P>,
  })

  async function handleManageFileData(row?: IFile) {
    const result = await handleManageFile(row)
    if (!result) {
      return
    }
    if (row) {
      loadFiles()
    } else {
      filesFormField.setValue([...(filesFormField.getValue() ?? []), result.id])
    }
  }

  async function handleRemoveFile(fileData: IFile) {
    if (!fileData?.id) {
      return
    }
    try {
      const confirmed = await handleConfirmDelete()
      if (!confirmed) {
        return
      }
      await skeem.mutate<IFile>({
        files: {
          destroy: fileData.id,
        },
      })
      filesField.setValue(filesField.getValue().filter(v => fileData.id !== v))
      alerts.genericSuccess("File removed")
    } catch (e) {
      console.error(e)
      alerts.genericSuccess("An error occurred removing file")
    }
  }

  return (
    <FloatingBox
      header={<H4>Files</H4>}
      footer={
        <ButtonsWrapper>
          <CreateButton onClick={() => handleManageFileData()}>Add File</CreateButton>
        </ButtonsWrapper>
      }
    >
      {confirmDeleteNode}
      {files === null ? (
        <LoadingTable />
      ) : files?.length === 0 ? (
        <P color="#999" align="center">
          Files attached to this project will appear here.
        </P>
      ) : (
        <Table.HorizontalOverflow minWidth={600}>
          <Table data={files ?? []}>
            <FileboyFilePreviewColumn field="fileboyId" />
            <Table.DataColumn field="fileName" heading="Name" />
            <Table.Column>
              {row => (
                <DownloadLinkButton
                  to={fileboy.buildFileUrl(row.fileboyId, {
                    download: true,
                    filename: row.fileName,
                  })}
                  extendTheme={{ padding: { topAndBottom: 8, sides: 12 } }}
                >
                  Download
                </DownloadLinkButton>
              )}
            </Table.Column>
            <Table.Column
              css={css`
                width: 1px;
              `}
            >
              {row => (
                <Grid columns={["max-content", "max-content"]} gap={8}>
                  <IconButton
                    icon={icons.edit}
                    onClick={() => handleManageFileData(row)}
                    disabled={row._delete}
                    buttonStyle="text"
                  />
                  <IconButton
                    icon={icons.delete}
                    buttonColor={"red"}
                    onClick={() => handleRemoveFile(row)}
                    buttonStyle="text"
                  />
                </Grid>
              )}
            </Table.Column>
          </Table>
        </Table.HorizontalOverflow>
      )}
    </FloatingBox>
  )
}

type IContact = IRecord<"contacts", "name" | "email">

function ContactsLoader({ contactIds }: { contactIds: string[] }) {
  const contactsField = Form2.useField<string[]>("contacts", { subscribe: true })
  const skeem = window.skeem
  const [contacts, setContacts] = useState<IContact[]>([])

  async function loadContacts() {
    if (!contactIds.length) {
      setContacts([])
      return
    }
    const contacts = await skeem.fetch2<IContact[]>({
      model: {
        name: "contacts",
        attributes: ["name", "email"],
        filter: { matches: { id: { or: contactIds } } },
      },
    })
    setContacts(contacts)
  }

  useAsyncEffect(async () => {
    await loadContacts()
  }, [JSON.stringify(contactIds)])

  return (
    <Table data={contacts}>
      <Table.Column>
        {contact => (
          <div>
            <div>{contact?.name}</div>
            <div
              css={css`
                color: #333;
                font-size: 12px;
                font-weight: 500;
              `}
            >
              {contact?.email ?? "No email"}
            </div>
          </div>
        )}
      </Table.Column>
      <Table.Column
        css={css`
          width: 1px;
        `}
      >
        {row => (
          <Grid columns={["max-content"]} gap={8}>
            <IconButton
              icon={icons.delete}
              buttonColor={"red"}
              onClick={() => {
                contactsField.setValue(contactsField.getValue().filter(v => v !== row.id))
              }}
              buttonStyle="text"
            />
          </Grid>
        )}
      </Table.Column>
    </Table>
  )
}

type IQuote = IRecord<
  "quotes",
  "identifier" | "customer_reference" | "carriage_pennies" | "customer_purchase_order_reference"
> & {
  items: IRecord<"quote_items", "price_pennies" | "quantity" | "discountPercent">[]
  customer: IRecord<"customers", "tax_rate" | "currency_name">
}
const iQuoteAttributes = [
  "identifier",
  "customer_reference",
  "customer_purchase_order_reference",
  // attrs required for getTotals
  { name: "items", attributes: ["price_pennies", "quantity", "discountPercent"] },
  "carriage_pennies",
  { name: "customer", attributes: ["tax_rate", "currency_name"] },
  //
]

function QuotesLoader({ quoteIds }: { quoteIds: string[] }) {
  const quotesField = Form2.useField<string[]>("quotes", { subscribe: true })
  const skeem = window.skeem
  const [contacts, setContacts] = useState<IQuote[]>([])

  async function loadQuotes() {
    if (!quoteIds.length) {
      setContacts([])
      return
    }
    const contacts = await skeem.fetch2<IQuote[]>({
      model: {
        name: "quotes",
        attributes: iQuoteAttributes,
        sort: { by: "created_at", direction: "desc" },
        filter: { matches: { id: { or: quoteIds } } },
      },
    })
    setContacts(contacts)
  }

  useAsyncEffect(async () => {
    await loadQuotes()
  }, [JSON.stringify(quoteIds)])

  return (
    <Table data={contacts}>
      <Table.Column heading="Quote">{quote => <HoverQuoteLoader id={quote.id} />}</Table.Column>
      <Table.Column heading="Total Price">
        {record => {
          const currency = currencyNameToSymbol(record.customer?.currency_name ?? "")
          return formatPrice(
            getTotals(record.items, record.carriage_pennies, record.customer?.tax_rate)
              .totalPricePennies,
            { isPennies: true, currency },
          )
        }}
      </Table.Column>
      <Table.DataColumn field="customer_reference" heading="Cust. Ref" />
      <Table.Column
        css={css`
          width: 1px;
        `}
      >
        {row => (
          <Grid columns={["max-content"]} gap={8}>
            <IconButton
              icon={icons.delete}
              buttonColor={"red"}
              onClick={() => {
                quotesField.setValue(quotesField.getValue().filter(v => v !== row.id))
              }}
              buttonStyle="text"
            />
          </Grid>
        )}
      </Table.Column>
    </Table>
  )
}

interface IFileData {
  fileName: string
  fileboyId: string
}

function useProjectsFiles() {
  const skeem = window.skeem
  const alerts = useSiteAlerts()
  return useConfirmation<undefined | IFile, IFile | null>(({ data, onResolve }) => {
    async function handleSubmit(fileData: IFileData) {
      if (data?.id) {
        await handleUpdateFile(fileData)
      } else {
        handleCreateFile(fileData)
      }
    }

    async function handleCreateFile(fileData: IFileData) {
      try {
        const newFile = await skeem.mutate<IFile>({
          files: {
            create: {
              fileName: fileData.fileName,
              fileboyId: fileData.fileboyId,
              fileType: fileTypes.file,
            },
          },
        })
        alerts.genericSuccess("File saved")
        onResolve(newFile)
      } catch (e) {
        console.error(e)
        alerts.genericError("An error occurred creating file")
      }
    }

    async function handleUpdateFile(fileData: IFileData) {
      if (!data?.id) {
        return
      }
      try {
        const updatedFile = await skeem.mutate<IFile>({
          files: {
            update: {
              id: data.id,
              fileName: fileData.fileName,
              fileboyId: fileData.fileboyId,
              fileType: fileTypes.file,
            },
          },
        })
        onResolve(updatedFile)
        alerts.genericSuccess("File saved")
      } catch (e) {
        console.error(e)
        alerts.genericSuccess("An error occurred updating file")
      }
    }

    return (
      <Modal
        render={() => (
          <div>
            <TextGroupMedium heading={data?.id ? `Edit File` : `Add File`} />
            <Form2.Form initialData={data} onSubmit={handleSubmit}>
              <Form2.Text field="fileName" validations={{ presence: true }} />
              <FormFileFieldValidations />
              <Form2.WithForm
                subscribe={{ dataChange: true, submitting: true }}
                render={form => (
                  <Grid gap={4}>
                    <div>
                      <Form2.FieldLabel fieldName="fileboyId" label={"File"} required />
                    </div>
                    <FileUploader
                      onFileUploaded={file => {
                        const data: Record<string, string> = { fileboyId: file.id }
                        const fileNameField = form.getField("fileName")
                        if (!fileNameField.getValue()) {
                          data.fileName = file.originalFilename
                        }
                        form.setPartialData(data)
                      }}
                      defaultFileId={form.getField("fileboyId").getValue()}
                    />
                    {form.getField("fileboyId").getError() && (
                      <P color="error" size="small">
                        {form.getField("fileboyId").getError()}
                      </P>
                    )}
                  </Grid>
                )}
              />
              <ConfirmCancelButtons
                onCancel={() => onResolve(null)}
                onConfirm="submit"
                buttonOpts={{ confirm: { children: "Submit" } }}
              />
            </Form2.Form>
          </div>
        )}
        isOpen
      />
    )
  })
}

function FormFileFieldValidations() {
  Form2.useFormSubmitLock(formState => {
    const idField = formState.getField("fileboyId")
    if (!idField.getValue()) {
      idField.setError("must be present")
      return false
    } else {
      idField.setError(undefined)
    }
  })
  return null
}

interface IValue {
  date: number
  value: number | null
}

function ValueFields() {
  const skeem = window.skeem

  const projectValueField = Form2.useField<number | null>("projectValue", {
    subscribe: true,
    defaultValue: null,
  })

  const field = Form2.useField<Record<string, IValue>>("rolloutValue", {
    defaultValue: {},
    subscribe: true,
  })
  const value = field.getValue()

  const customerField = Form2.useField<string>("customer", { subscribe: true })
  const [customer, setCustomer] = useState<IRecord<"customers", "currency_name"> | null>(null)

  const quotesField = Form2.useField<string[]>("quotes", { subscribe: true })
  const [quotesValue, setQuotesValue] = useState<number | null>(null)

  async function loadQuotes() {
    const quoteIds = quotesField.getValue()
    if (!quoteIds.length) {
      setQuotesValue(projectValueField.getValue())
      return
    }
    const quotes = await skeem.fetch2<
      (IRecord<"quotes", "carriage_pennies"> & {
        items: IRecord<"quote_items", "price_pennies" | "quantity" | "discountPercent">[]
        customer: IRecord<"customers", "tax_rate" | "currency_name">
      })[]
    >({
      model: {
        name: "quotes",
        attributes: [
          // attrs required for getTotals
          { name: "items", attributes: ["price_pennies", "quantity", "discountPercent"] },
          "carriage_pennies",
          { name: "customer", attributes: ["tax_rate", "currency_name"] },
          //
        ],
        sort: { by: "created_at", direction: "desc" },
        filter: { matches: { id: { or: quoteIds } } },
      },
    })
    const quoteValue = quotes.reduce(
      (total, quote) =>
        total +
        getTotals(quote.items, quote.carriage_pennies, quote.customer?.tax_rate ?? 0)
          .totalPricePennies,
      0,
    )

    setQuotesValue(quoteValue)
  }

  useAsyncEffect(async () => {
    await loadQuotes()
  }, [JSON.stringify(quotesField.getValue())])

  useAsyncEffect(async () => {
    const customerId = customerField.getValue()
    if (customerId) {
      const customer = await skeem.fetch2<IRecord<"customers", "currency_name">>({
        first: {
          name: "customers",
          attributes: ["currency_name"],
          filter: { matches: { id: customerId } },
        },
      })
      setCustomer(customer)
    }
  }, [customerField.getValue()])

  function handleChange(id: string, value: Partial<IValue>) {
    const currentValue = field.getValue()
    field.setValue({ ...currentValue, [id]: { ...currentValue[id], ...value } as any })
  }

  function handleAddMonth() {
    const value = field.getValue()
    const maxDate = Math.max(...Object.values(value).map(v => Number(v.date)))
    const date = add(new Date(maxDate ?? undefined), { months: 1 })
    field.setValue({ ...value, [Math.random().toString()]: { date: date.valueOf(), value: null } })
  }

  function handleRemove(id: string) {
    const value = { ...field.getValue() }
    delete value[id]
    field.setValue(value)
  }

  const rolloutValue = sum(Object.values(value), item => item.value ?? 0)

  const rolloutColor = (() => {
    const rollout = Math.round(rolloutValue)
    const quotes = Math.round(quotesValue ?? 0)
    console.log({ rollout, quotes })
    if (!quotes || !rollout) {
      return "black"
    }
    if (quotes === rollout) {
      return themeColors.green
    }
    if (rollout > quotes) {
      return themeColors.red
    }
    if (rollout < quotes) {
      return themeColors.orange
    }
    return "black"
  })()

  return (
    <FloatingBox
      header={
        <React.Fragment>
          <H4>Project Rollout</H4>
          <TextBlock size="small">
            <P>
              If a project rollout is specified, the graph on the projects index will use the values
              set below instead of the value of the quotes or the project value.
            </P>
          </TextBlock>
        </React.Fragment>
      }
      footer={
        <ButtonsWrapper>
          <Button onClick={() => handleAddMonth()}>Add date</Button>
        </ButtonsWrapper>
      }
    >
      <Grid gap={16}>
        {Object.entries(value)
          .sort((a, b) => {
            const valueA = Number(a[1].date)
            const valueB = Number(b[1].date)
            return valueA - valueB
          })
          .map(([id, value]) => (
            <Grid key={id} columns={[{ fr: 1 }, { fr: 1 }, "max-content"]} gap={8}>
              <Fields.Date2
                onChange={date => handleChange(id, { date: date?.valueOf() })}
                value={value.date ? new Date(value.date) : null}
              />
              <Fields.Price
                onChange={value => handleChange(id, { value })}
                value={value.value}
                inPennies
              />
              <IconButton
                icon={icons.delete}
                buttonColor="red"
                onClick={() => handleRemove(id)}
                square
              />
            </Grid>
          ))}
        {rolloutValue > 0 ? (
          <P weight={600} blockSpacing={0}>
            Rollout Total:{" "}
            <span
              css={css`
                color: ${rolloutColor};
              `}
            >
              {formatPrice(rolloutValue, {
                isPennies: true,
                currency: currencyNameToSymbol(customer?.currency_name ?? ""),
              })}
            </span>{" "}
            /{" "}
            {quotesValue ? (
              <span>
                {formatPrice(quotesValue, {
                  isPennies: true,
                  currency: currencyNameToSymbol(customer?.currency_name ?? ""),
                })}
              </span>
            ) : (
              "-"
            )}
          </P>
        ) : (
          <P color="#999" align="center">
            (Optional) Specify the value of this project at different dates
          </P>
        )}
        {quotesValue && quotesValue > 0 && rolloutValue > 0 && (
          <P>
            Remaining:{" "}
            <span>
              {formatPrice(quotesValue - rolloutValue, {
                isPennies: true,
                currency: currencyNameToSymbol(customer?.currency_name ?? ""),
              })}
            </span>
          </P>
        )}
      </Grid>
    </FloatingBox>
  )
}
